import type { SxProps, Theme, TypographyProps } from "@mui/material";
import { Box, Stack, Typography } from "@mui/material";
import type React from "react";

export interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  titleProps?: TypographyProps;
  subtitleProps?: TypographyProps;
}

/**
 * Unified page header with consistent styles across pages.
 * Renders a top row with title/subtitle on the left and actions on the right,
 * and an optional second row rendered via children (e.g., search bars, filters).
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  children,
  sx,
  titleProps,
  subtitleProps,
}: PageHeaderProps) {
  return (
    <Box
      sx={{
        mb: 1,
        pb: 3,
        ...sx,
      }}
    >
      <Stack spacing={3}>
        <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: subtitle ? 0.5 : 0,
                letterSpacing: "-0.02em",
              }}
              {...titleProps}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                }}
                {...subtitleProps}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {actions ? (
            <Stack direction="row" spacing={0} alignItems="center">
              {actions}
            </Stack>
          ) : null}
        </Stack>
        {children ? children : null}
      </Stack>
    </Box>
  );
}

export default PageHeader;
